/**
 * A self-serve customer's own plan, read-only: hires included, used and
 * remaining, what they have paid, and the balance left. Shown to the
 * customer's own Admin.
 *
 * Reads two endpoints in parallel. Both let an ADMIN read only their own
 * customer, and only a SELF_SERVE plan; an enterprise customer's terms stay
 * Avrio-internal and come back 404, exactly like no plan at all:
 * - GET /api/customers/{customerId}/plan (avrio-backend#176), the stored
 *   terms. A 404 here renders nothing, not an error.
 * - GET /api/customers/{customerId}/plan/usage (avrio-backend#178), usage.
 *   If only this one fails, the terms still show and the usage figures say
 *   they could not be loaded, rather than the whole panel going away or a
 *   balance being guessed from the terms.
 */

import { memo, useCallback, useEffect, useState } from 'react';
import { Alert, Button, Spin, Typography } from 'antd';
import axios from '../../../../axiosConfig';
import { CustomerPlan, CustomerPlanUsage } from '../../types';
import { summarisePlan, summariseUsage } from './summary';
import styles from './index.module.css';

const { Title, Text } = Typography;

const money = (amount: number | null, currency: string) => {
  if (amount === null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
};

const count = (n: number | null) => (n === null ? '—' : String(n));

const Figure = ({ label, value }: { label: string; value: string }) => (
  <div>
    <dt className={styles.label}>{label}</dt>
    <dd>{value}</dd>
  </div>
);

const YourPlanSection = ({ customerId }: { customerId: number }) => {
  const [plan, setPlan] = useState<CustomerPlan | null>(null);
  const [usage, setUsage] = useState<CustomerPlanUsage | null>(null);
  const [usageFailed, setUsageFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setUsageFailed(false);
    Promise.allSettled([
      axios.get<CustomerPlan>(`/api/customers/${customerId}/plan`),
      axios.get<CustomerPlanUsage>(`/api/customers/${customerId}/plan/usage`),
    ]).then(([planRes, usageRes]) => {
      if (cancelled) return;
      if (planRes.status === 'fulfilled') {
        setPlan(planRes.value.data);
      } else {
        setPlan(null);
        if (planRes.reason?.response?.status !== 404) setError(true);
      }
      if (usageRes.status === 'fulfilled') {
        setUsage(usageRes.value.data);
      } else {
        setUsage(null);
        setUsageFailed(true);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  useEffect(() => load(), [load]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spin size="small" />
        <Text type="secondary">Loading your plan…</Text>
      </div>
    );
  }
  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load your plan."
        action={
          <Button size="small" onClick={load}>
            Try again
          </Button>
        }
        className={styles.section}
      />
    );
  }
  if (!plan) return null;

  const s = summarisePlan(plan, usage);
  const u = usage ? summariseUsage(usage) : null;

  return (
    <section className={styles.section} aria-labelledby="your-plan-title">
      <Title level={5} id="your-plan-title" className={styles.title}>
        Your plan
      </Title>

      <dl className={styles.figures} aria-label="Hires">
        <Figure label="Hires included" value={count(s.hiresIncluded)} />
        {u && (
          <>
            <Figure label="Hires used" value={count(u.hiresUsed)} />
            <Figure
              label="Hires remaining"
              value={
                u.hiresOver > 0
                  ? `0 (${u.hiresOver} over plan)`
                  : count(u.hiresRemaining)
              }
            />
          </>
        )}
      </dl>

      <dl className={styles.figures} aria-label="Payments">
        <Figure
          label="Rate per hire"
          value={money(s.ratePerHire, s.currency)}
        />
        <Figure
          label="Contract value"
          value={money(s.contractValue, s.currency)}
        />
        <Figure label="Paid" value={money(s.paid, s.currency)} />
        {u && (
          <Figure
            label="Balance remaining"
            value={money(u.balanceRemaining, s.currency)}
          />
        )}
        {u && u.overageAmount > 0 && (
          <Figure
            label="Over-plan hires billed"
            value={money(u.overageAmount, s.currency)}
          />
        )}
      </dl>

      {usageFailed && (
        <Alert
          type="warning"
          showIcon
          message="Hires used and your remaining balance could not be loaded."
          action={
            <Button size="small" onClick={load}>
              Try again
            </Button>
          }
          className={styles.usageWarning}
        />
      )}

      <Text type="secondary" className={styles.note}>
        Plan started {new Date(plan.effectiveFrom).toLocaleDateString()}. A hire
        counts when you make an offer through Avrio, and is invoiced at the rate
        above against your remaining balance. Hires past your plan are still
        allowed and are billed at the same rate.
      </Text>
    </section>
  );
};

export default memo(YourPlanSection);
